import { useQuery } from "@tanstack/react-query";

async function failure(res: Response, fallback: string): Promise<Error> {
  const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return new Error(typeof json?.error === "string" && json.error ? json.error : fallback);
}

export interface CompetencyAggregateRow {
  competencyId: string;
  competencyName: string;
  categoryName: string | null;
  categoryColor: string | null;
  population: number;
  averageOrdinal: number;
  belowMeetsCount: number;
  belowMeetsPercent: number;
  hasExpectation: boolean;
  variance: number | null;
}

/** `roleLevelId` is `null` for "All levels", `"none"` for "no level recorded". */
export interface CompetencyAggregateGroup {
  roleLevelId: string | null;
  roleLevelLabel: string;
  competencies: CompetencyAggregateRow[];
}

function cycleParam(cycleIds: string[]): string {
  return cycleIds.length > 0 ? `&cycleIds=${cycleIds.join(",")}` : "";
}

/** `null` is "every level" (the default); `"none"` is the no-level-recorded bucket. */
function levelParam(roleLevelId: string | null): string {
  return roleLevelId ? `&roleLevelId=${roleLevelId}` : "";
}

/**
 * How a whole role performed against each competency — EPIC-071.
 *
 * `categoryId` of null is every competency, including ones carrying no category
 * (WI-318) — the category is a lens on the report, never a prerequisite for
 * appearing in it. `roleLevelId` of null is every level, the existing
 * breakdown (WI-317) — set to narrow the report to one.
 */
export function useRoleCompetencyAggregates(
  roleId: string | null,
  cycleIds: string[],
  categoryId: string | null = null,
  roleLevelId: string | null = null,
) {
  return useQuery({
    queryKey: ["role-competency-report", roleId, cycleIds, categoryId, roleLevelId],
    queryFn: async (): Promise<CompetencyAggregateGroup[]> => {
      const res = await fetch(
        `/api/reports/role-competency?roleId=${roleId}${cycleParam(cycleIds)}${
          categoryId ? `&categoryId=${categoryId}` : ""
        }${levelParam(roleLevelId)}`,
      );
      if (!res.ok) throw await failure(res, "Failed to load the competency report");
      return (await res.json()).data;
    },
    enabled: Boolean(roleId),
  });
}

export interface RoleCompetencyOverviewRow {
  roleId: string;
  roleName: string;
  averageOrdinal: number | null;
  population: number;
  meetsMinimum: boolean;
}

/**
 * Every role's average score, one figure each — the comparison entry point,
 * WI-319. Respects the same cycle, category and level filters as the single-role
 * breakdown, so picking a bar lands on numbers that already agree.
 */
export function useAllRolesCompetencyOverview(
  cycleIds: string[],
  categoryId: string | null = null,
  roleLevelId: string | null = null,
) {
  return useQuery({
    queryKey: ["role-competency-report", "overview", cycleIds, categoryId, roleLevelId],
    queryFn: async (): Promise<RoleCompetencyOverviewRow[]> => {
      const parts = [
        cycleIds.length > 0 ? `cycleIds=${cycleIds.join(",")}` : "",
        categoryId ? `categoryId=${categoryId}` : "",
        roleLevelId ? `roleLevelId=${roleLevelId}` : "",
      ].filter(Boolean);
      const res = await fetch(
        `/api/reports/role-competency/overview${parts.length > 0 ? `?${parts.join("&")}` : ""}`,
      );
      if (!res.ok) throw await failure(res, "Failed to load the role comparison");
      return (await res.json()).data;
    },
  });
}

export interface CompetencyAggregateContributor {
  personId: string;
  personName: string;
  averageOrdinal: number;
}

/** The people behind one aggregate figure — EPIC-071 WI-269. */
export function useCompetencyAggregateContributors(
  roleId: string | null,
  roleLevelId: string | null,
  competencyId: string | null,
  cycleIds: string[],
  enabled: boolean,
) {
  return useQuery({
    queryKey: [
      "role-competency-report",
      "contributors",
      roleId,
      roleLevelId,
      competencyId,
      cycleIds,
    ],
    queryFn: async (): Promise<CompetencyAggregateContributor[]> => {
      const roleLevelParam = roleLevelId === null ? "all" : roleLevelId;
      const res = await fetch(
        `/api/reports/role-competency/contributors?roleId=${roleId}&roleLevelId=${roleLevelParam}&competencyId=${competencyId}${cycleParam(cycleIds)}`,
      );
      if (!res.ok) throw await failure(res, "Failed to load who's behind this figure");
      return (await res.json()).data;
    },
    enabled: enabled && Boolean(roleId) && Boolean(competencyId),
  });
}

/**
 * What the viewer may report on — everything (HR/admin) or a pillar head's
 * pillars and roles (EPIC-076 WI-284). An error means "no access", so it is not
 * retried and callers treat it as absence rather than a failure to show.
 */
export type CompetencyReportScope =
  | { mode: "all" }
  | {
      mode: "pillar";
      pillars: { id: string; name: string }[];
      roles: { id: string; name: string }[];
    };

export function useCompetencyReportScope(enabled = true) {
  return useQuery({
    queryKey: ["competency-report", "scope"] as const,
    queryFn: async (): Promise<CompetencyReportScope> => {
      const res = await fetch("/api/reports/role-competency/scope");
      if (!res.ok) throw await failure(res, "No access to the competency report");
      return (await res.json()).data;
    },
    enabled,
    retry: false,
  });
}

/** The closed rounds the report can be narrowed to — names only. */
export function useCompetencyReportCycles() {
  return useQuery({
    queryKey: ["competency-report", "cycles"] as const,
    queryFn: async (): Promise<{ id: string; name: string }[]> => {
      const res = await fetch("/api/reports/role-competency/cycles");
      if (!res.ok) throw await failure(res, "Failed to load the closed rounds");
      return (await res.json()).data;
    },
  });
}
